<template>
    <div class="bg-bg min-h-dvh p-4">
        <div class="flex min-h-full items-center justify-center">
            <div class="w-full max-w-md">
                <!-- Logo and Title -->
                <div class="mb-8 text-center">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 44 47.98"
                        class="mb-8 inline-block size-24"
                    >
                        <path
                            fill="#fff"
                            d="M21.1 12.69v3.8c-3.4.5-6.1 3.8-6.1 7.5l-3.6.5c-.01-5.75 4.06-10.7 9.7-11.8z"
                        ></path>
                        <path
                            fill="#fff"
                            d="M21.1 18.39v4.6l-4.4.7c0-2.7 2-5 4.4-5.4v.1zm0-11.4v3.8c-6.3.9-11.4 7.1-11.4 14l-3.5.4c0-8.9 6.7-17 15-18.2h-.1zm1.7 11.3c2.4-.4 4.4 1.5 4.4 4l-4.4.7v-4.7zm0-11.5c8.3-1.1 15 5.2 15 14.1l-3.6.5c0-6.8-5.1-11.6-11.4-10.8v-3.8zm21 14.8c0 12.6-9.5 24.2-21.1 25.8v-4.7c9.2-1.3 16.7-10.5 16.7-20.4l4.4-.7zm-11.4 1.7c0 5.7-4.4 11-9.7 11.8v-3.8c3.4-.5 6.2-3.9 6.2-7.5l3.5-.5z"
                        ></path>
                        <path
                            fill="#fff"
                            d="M27.1 23.99c0 2.6-2 5-4.4 5.4v-4.8l4.4-.6zM22.8.19C34.5-1.41 44 7.39 44 19.99l-4.4.7c0-10-7.5-17-16.7-15.8V.19h-.1zM9.7 26.59c0 6.8 5 11.6 11.4 10.7v3.9c-8.1 1.1-15-5.2-15-14.2l3.6-.5v.1z"
                        ></path>
                        <path
                            fill="#fff"
                            d="M15 25.89c0 3.6 2.7 6.2 6.1 5.8v3.8c-5.3.7-9.7-3.4-9.7-9.1l3.6-.5zm-10.6 1.4c0 10 7.5 17 16.7 15.8v4.7C9.4 49.39 0 40.59 0 27.99l4.4-.7z"
                        ></path>
                    </svg>
                    <h1 class="panel-title text-center text-white">{{ $t('login.title') }}</h1>
                </div>

                <!-- Login Form Panel -->
                <Panel size="full">
                    <form @submit.prevent="handleLogin" class="space-y-6">
                        <!-- Email Field -->
                        <div>
                            <label for="email" class="mb-2 block text-sm font-medium text-white">
                                {{ $t('login.email_label') }}
                            </label>
                            <input
                                id="email"
                                v-model="email"
                                name="email"
                                type="email"
                                autocomplete="email"
                                required
                                class="bg-card w-full border border-zinc-700 px-4 py-3 text-white transition-colors focus:border-red-500 focus:outline-none"
                                :style="{
                                    clipPath: 'polygon(8px 0, 100% 0, 100% 100%, 0 100%, 0 8px)',
                                }"
                                :placeholder="$t('login.email_placeholder')"
                            />
                        </div>

                        <!-- Password Field -->
                        <div>
                            <label for="password" class="mb-2 block text-sm font-medium text-white">
                                {{ $t('login.password_label') }}
                            </label>
                            <input
                                id="password"
                                v-model="password"
                                name="password"
                                type="password"
                                autocomplete="current-password"
                                required
                                class="bg-card w-full border border-zinc-700 px-4 py-3 text-white transition-colors focus:border-red-500 focus:outline-none"
                                :style="{
                                    clipPath: 'polygon(8px 0, 100% 0, 100% 100%, 0 100%, 0 8px)',
                                }"
                                :placeholder="$t('login.password_placeholder')"
                            />
                        </div>

                        <!-- Error Message -->
                        <div
                            v-if="errorMessage"
                            class="border border-red-600 bg-red-900/50 p-4"
                            :style="{
                                clipPath: 'polygon(8px 0, 100% 0, 100% 100%, 0 100%, 0 8px)',
                            }"
                        >
                            <div class="text-sm text-red-300" data-testid="login-error-message">
                                {{ errorMessage }}
                            </div>
                        </div>

                        <!-- Login Button -->
                        <div class="pt-2">
                            <DiagonalCutButton
                                type="submit"
                                :disabled="isLoading"
                                :loading="isLoading"
                                variant="primary"
                                cut-position="right-top"
                                class="w-full"
                                data-testid="login-submit-button"
                                @click="handleLogin"
                            >
                                {{ isLoading ? $t('login.submitting') : $t('login.submit') }}
                            </DiagonalCutButton>
                        </div>
                    </form>
                </Panel>
            </div>
        </div>
    </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth.js'
import Panel from '@/components/layout/Panel.vue'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'

export default {
    name: 'LoginView',
    components: {
        Panel,
        DiagonalCutButton,
    },
    data() {
        return {
            email: '',
            password: '',
            isLoading: false,
            errorMessage: '',
        }
    },
    methods: {
        async handleLogin() {
            if (!this.email || !this.password) {
                this.errorMessage = this.$t('login.error_missing_fields')
                return
            }

            this.isLoading = true
            this.errorMessage = ''

            try {
                const authStore = useAuthStore()
                const success = await authStore.loginWithEmailPassword(this.email, this.password)

                if (success) {
                    this.$router.push({ name: 'home' })
                } else {
                    this.errorMessage = this.$t('login.error_failed')
                }
            } catch (error) {
                console.error('Login error:', error)
                this.errorMessage = this.$t('login.error_generic')
            } finally {
                this.isLoading = false
            }
        },
    },
}
</script>
